import * as React from 'react';
import { useState } from 'react';
import { Form, Formik } from 'formik';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from '../../../../../../consts/report';
import {
  getReportDefaultGendersForMultiSelect,
  getReportAgesForMultiSelect
} from '../../../../../../helpers/multiselect/multiselect';
import { School } from '../../../../../../models/school';
import { SchoolForm } from '../../../../../../models/form';
import { getStartDateForSchoolYear } from '../../../../../../helpers/calendar/calendar';
import '../SchoolEvents.scss';
import {
  ReportActions,
  ReportCheckboxGrid,
  ReportDateRange,
  ReportDescription,
  ReportSection,
  ReportToggle,
  ReportToggleList
} from '../SchoolEventView/Reports/ReportFormUi';

interface Props {
  onCloseClick: () => void;
  onSubmit: (data: any, reportType: string) => void;
  school: School;
  forms: SchoolForm[];
}

// report 007
export function FixtureParticipationByStudentsReport({ onCloseClick, onSubmit, school, forms }: Props) {
  const now = new Date();
  let dateFrom = getStartDateForSchoolYear(now);
  const genders = getReportDefaultGendersForMultiSelect();
  const ages = getReportAgesForMultiSelect(school, forms);

  const [isAgesAndGendersChecked, setIsAgesAndGendersChecked] = useState(true);

  const requestInitial = {
    dateFrom,
    dateTo: now,
    isShowZero: false,
    isShowNonZero: true,
    isPrintPP: false,
    isPrintSEN: false,
    isPrintFSM: false,
    isGroupBySports: true,
    genders,
    ages,
    isSelectAllAges: true
  };

  const onGendersChange = (index, values, setFieldValue) => {
    const genderItems = values.genders;
    const ageItems = values.ages;

    const genderItemsUpdated = genderItems.map((genderItem, genderIndex) => {
      return genderIndex === index
        ? {
            ...genderItem,
            isChecked: !genderItem.isChecked
          }
        : genderItem;
    });

    const gendersChecked = genderItemsUpdated.map(gender => gender.isChecked);
    const agesChecked = ageItems.map(age => age.isChecked);
    const isCheckedGendersExist = gendersChecked.includes(true);
    const isCheckedAgesExist = agesChecked.includes(true);

    setIsAgesAndGendersChecked(isCheckedGendersExist && isCheckedAgesExist);
    setFieldValue('genders', genderItemsUpdated);
  };

  const onAgesChange = (index, values, setFieldValue) => {
    const ageItems = values.ages;
    const genderItems = values.genders;

    const ageItemsUpdated = ageItems.map((ageItem, ageIndex) => {
      return ageIndex === index
        ? {
            ...ageItem,
            isChecked: !ageItem.isChecked
          }
        : ageItem;
    });

    const gendersChecked = genderItems.map(gender => gender.isChecked);
    const agesChecked = ageItemsUpdated.map(age => age.isChecked);
    const isCheckedGendersExist = gendersChecked.includes(true);
    const isCheckedAgesExist = agesChecked.includes(true);

    const isSelectAllAges = ageItemsUpdated.every(age => age.isChecked === true);

    setIsAgesAndGendersChecked(isCheckedAgesExist && isCheckedGendersExist);
    setFieldValue('isSelectAllAges', isSelectAllAges);
    setFieldValue('ages', ageItemsUpdated);
  };

  const onSelectAllAgesChange = (event, values, setFieldValue) => {
    const isSelectAllAges = event.target.checked;
    const ageItems = values.ages;
    const genderItems = values.genders;

    const ageItemsUpdated = ageItems.map(age => {
      return {
        ...age,
        isChecked: isSelectAllAges
      };
    });

    const gendersChecked = genderItems.map(gender => gender.isChecked);
    const agesChecked = ageItemsUpdated.map(age => age.isChecked);
    const isCheckedGendersExist = gendersChecked.includes(true);
    const isCheckedAgesExist = agesChecked.includes(true);

    setIsAgesAndGendersChecked(isCheckedAgesExist && isCheckedGendersExist);
    setFieldValue('isSelectAllAges', isSelectAllAges);
    setFieldValue('ages', ageItemsUpdated);
  };

  return (
    <Formik
      onSubmit={values =>
        onSubmit(values, REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.FIXTURE_PARTICIPATION_STATISTICS_BY_STUDENTS)
      }
      initialValues={requestInitial}
    >
      {({ values, setFieldValue }) => (
        <Form className="eReportForm">
          <ReportDescription>
            View how many fixtures each student participated in during the selected period and include relevant student
            information.
          </ReportDescription>
          <ReportSection number={1} title="Reporting period" hint="Choose the date range covered by the report.">
            <ReportDateRange
              dateFrom={values.dateFrom}
              dateTo={values.dateTo}
              onChangeFrom={date => {
                setFieldValue('dateFrom', date);
                if (values.dateTo < date) setFieldValue('dateTo', date);
              }}
              onChangeTo={date => setFieldValue('dateTo', date)}
            />
          </ReportSection>
          <ReportSection number={2} title="Participation" hint="Choose which students and grouping should appear.">
            <ReportToggleList>
              <ReportToggle
                name="isShowZero"
                label="Show non-participants"
                hint="Include students who did not participate in any fixture."
                value={values.isShowZero}
                onChange={value => setFieldValue('isShowZero', value)}
              />
              <ReportToggle
                name="isShowNonZero"
                label="Show participants"
                hint="Include students who participated in fixtures."
                value={values.isShowNonZero}
                onChange={value => setFieldValue('isShowNonZero', value)}
              />
              <ReportToggle
                name="isGroupBySports"
                label="Group by sports and activities"
                hint="Group fixture totals by sport or activity."
                value={values.isGroupBySports}
                onChange={value => setFieldValue('isGroupBySports', value)}
              />
            </ReportToggleList>
          </ReportSection>
          <ReportSection number={3} title="Student details" hint="Choose additional student information to include.">
            <ReportToggleList>
              <ReportToggle
                name="isPrintPP"
                label="Show PP"
                hint="Include PP information."
                value={values.isPrintPP}
                onChange={value => setFieldValue('isPrintPP', value)}
              />
              <ReportToggle
                name="isPrintSEN"
                label="Show SEN"
                hint="Include SEN information."
                value={values.isPrintSEN}
                onChange={value => setFieldValue('isPrintSEN', value)}
              />
              <ReportToggle
                name="isPrintFSM"
                label="Show FSM"
                hint="Include FSM information."
                value={values.isPrintFSM}
                onChange={value => setFieldValue('isPrintFSM', value)}
              />
            </ReportToggleList>
          </ReportSection>
          <ReportSection number={4} title="Gender" hint="Select at least one gender category.">
            <ReportCheckboxGrid
              items={values.genders}
              onChange={index => onGendersChange(index, values, setFieldValue)}
            />
          </ReportSection>
          <ReportSection number={5} title="Age groups" hint="Select the age groups included in the report.">
            <ReportToggleList>
              <ReportToggle
                name="isSelectAllAges"
                label="Select all ages"
                hint="Include every available age group."
                value={values.isSelectAllAges}
                onChange={value => onSelectAllAgesChange({ target: { checked: value } }, values, setFieldValue)}
              />
            </ReportToggleList>
            <ReportCheckboxGrid items={values.ages} onChange={index => onAgesChange(index, values, setFieldValue)} />
          </ReportSection>
          <ReportActions onCancel={onCloseClick} disabled={!isAgesAndGendersChecked} />
        </Form>
      )}
    </Formik>
  );
}
